import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronLeft, Search, X, Image as ImageIcon, Package, Edit, Save, Plus, Trash2 } from 'lucide-react';
import { spacing, fontSize, fontWeight, borderRadius, shadows } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';
import WebvonkLoader from '../components/WebvonkLoader';
import { useTheme } from '../../contexts/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import BackButton from '../components/BackButton';

const Container = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing.md};
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  @media (max-width: 768px) {
    padding: ${spacing.sm};
  }
`;


const HeaderTitle = styled.h1`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  flex: 1;
  text-align: center;
  margin-right: 40px;
`;

const SearchContainer = styled.div`
  padding: ${spacing.md};
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.text};
  margin-left: ${spacing.sm};
  
  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  display: flex;
  align-items: center;
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const Content = styled.div`
  flex: 1;
  padding: ${spacing.lg};
  height: calc(100vh - 160px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  
  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const ProductDetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: ${props => props.theme.colors.surface};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xl};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
  padding-bottom: ${spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ProductImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: ${borderRadius.lg};
  border: 2px solid ${props => props.theme.colors.border};
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h2`
  font-size: ${fontSize.xxl};
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.sm};
`;

const ProductMeta = styled.div`
  display: flex;
  gap: ${spacing.lg};
  margin-bottom: ${spacing.md};
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const MetaLabel = styled.span`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.muted};
  font-weight: ${fontWeight.medium};
`;

const MetaValue = styled.span`
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.text};
  font-weight: ${fontWeight.semibold};
`;

const Section = styled.div`
  margin-bottom: ${spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const FormLabel = styled.label`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const FormInput = styled.input`
  padding: ${spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.brand};
  }
`;

const FormTextarea = styled.textarea`
  padding: ${spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.background};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.brand};
  }
`;

const FormSelect = styled.select`
  padding: ${spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.brand};
  }
`;

const FormCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: var(--accent);
  }
  
  label {
    color: ${props => props.theme.colors.text};
    font-size: ${fontSize.md};
    cursor: pointer;
  }
`;

const SaveButton = styled.button`
  background: var(--accent);
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  padding: ${spacing.md} ${spacing.xl};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  transition: all 0.2s ease;
  align-self: flex-start;
  
  &:hover {
    background: #e55a00;
    transform: translateY(-1px);
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const OptionCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
`;

const OptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.md};
`;

const OptionTitle = styled.h4`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const OptionActions = styled.div`
  display: flex;
  gap: ${spacing.sm};
`;

const IconButton = styled.button`
  background: ${props => props.danger ? props.theme.colors.error : props.theme.colors.brand};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const OptionDetails = styled.p`
  color: ${props => props.theme.colors.muted};
  font-size: ${fontSize.sm};
  margin: ${spacing.xs} 0;
`;

const AddButton = styled.button`
  background: ${props => props.theme.colors.success};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  padding: ${spacing.md} ${spacing.lg};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xl} 0;
  font-size: ${fontSize.lg};
  color: ${props => props.theme.colors.muted};
`;

export default function ProductDetailPage() {
  const { colors } = useTheme();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { id: productId } = useParams();
  
  console.log('🔍 ProductDetailPage - productId:', productId);
  console.log('🔍 Full params:', useParams());
  console.log('🔍 Current URL:', window.location.pathname);
  
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [productCustomizations, setProductCustomizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_cents: '',
    image_url: '',
    sort_index: '',
    category_id: '',
    Productnummer: '',
    toon_extra_saus: false,
    product_customizations: false
  });

  useEffect(() => {
    console.log('🔄 ProductDetailPage useEffect - productId:', productId);
    
    if (!productId) {
      console.log('❌ Geen productId, navigeer naar product-status');
      navigate('/KMFC/settings/product-status');
      return;
    }
    
    if (productId) {
      loadProduct();
      loadCategories();
      loadProductOptions();
      loadProductCustomizations();
    }
  }, [productId]);

  const loadProduct = async () => {
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) return;

      const { data, error } = await kmfcClient
        .from('products')
        .select(`
          *,
          categories (
            name,
            position
          )
        `)
        .eq('id', productId)
        .single();

      if (error) throw error;
      
      setProduct(data);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        price_cents: data.price_cents || '',
        image_url: data.image_url || '',
        sort_index: data.sort_index || '',
        category_id: data.category_id || '',
        Productnummer: data.Productnummer || '',
        toon_extra_saus: data.toon_extra_saus || false,
        product_customizations: data.product_customizations || false
      });
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) return;

      const { data, error } = await kmfcClient
        .from('categories')
        .select('*')
        .order('position');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProductOptions = async () => {
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) return;

      const { data, error } = await kmfcClient
        .from('product_options')
        .select(`
          *,
          product_option_values (*)
        `)
        .eq('product_id', productId)
        .order('position');

      if (error) throw error;
      setProductOptions(data || []);
    } catch (error) {
      console.error('Error loading product options:', error);
    }
  };

  const loadProductCustomizations = async () => {
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) return;

      const { data, error } = await kmfcClient
        .from('product_customizations')
        .select('*')
        .eq('product_id', productId)
        .order('sort_index');

      if (error) throw error;
      setProductCustomizations(data || []);
    } catch (error) {
      console.error('Error loading product customizations:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProduct = async () => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen producten bewerken');
      return;
    }

    setSaving(true);
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('products')
        .update({
          name: formData.name,
          description: formData.description,
          price_cents: formData.price_cents,
          image_url: formData.image_url,
          sort_index: formData.sort_index,
          category_id: formData.category_id,
          Productnummer: formData.Productnummer,
          toon_extra_saus: formData.toon_extra_saus,
          product_customizations: formData.product_customizations
        })
        .eq('id', productId);

      if (error) throw error;

      alert('Product succesvol opgeslagen!');
      navigate('/KMFC/settings/product-status');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Fout bij opslaan van product');
    } finally {
      setSaving(false);
    }
  };

  const editProductOption = (optionId) => {
    const option = productOptions.find(opt => opt.id === optionId);
    if (!option) return;

    const name = prompt('Optie naam:', option.name);
    if (name === null) return;

    const type = prompt('Optie type (single/multiple):', option.type);
    if (type === null) return;

    const required = window.confirm('Is deze optie verplicht?');
    const minSelect = prompt('Minimale selectie:', option.min_select || 0);
    const maxSelect = prompt('Maximale selectie:', option.max_select || 1);

    updateProductOption(optionId, {
      name,
      type: type === 'multiple' ? 'multiple' : 'single',
      required,
      min_select: parseInt(minSelect) || 0,
      max_select: parseInt(maxSelect) || 1
    });
  };

  const updateProductOption = async (optionId, updates) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen opties bewerken');
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('product_options')
        .update(updates)
        .eq('id', optionId);

      if (error) throw error;

      await loadProductOptions();
      alert('Optie succesvol bijgewerkt!');
    } catch (error) {
      console.error('Error updating product option:', error);
      alert('Fout bij bijwerken van optie');
    }
  };

  const addProductOption = () => {
    const name = prompt('Optie naam:');
    if (!name) return;

    const type = prompt('Optie type (single/multiple):', 'single');
    if (!type) return;

    const required = window.confirm('Is deze optie verplicht?');
    const minSelect = prompt('Minimale selectie:', '0');
    const maxSelect = prompt('Maximale selectie:', '1');

    createProductOption({
      name,
      type: type === 'multiple' ? 'multiple' : 'single',
      required,
      min_select: parseInt(minSelect) || 0,
      max_select: parseInt(maxSelect) || 1,
      position: productOptions.length
    });
  };

  const createProductOption = async (optionData) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen opties toevoegen');
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('product_options')
        .insert({
          ...optionData,
          product_id: productId
        });

      if (error) throw error;

      await loadProductOptions();
      alert('Optie succesvol toegevoegd!');
    } catch (error) {
      console.error('Error creating product option:', error);
      alert('Fout bij toevoegen van optie');
    }
  };

  const editProductCustomization = (customId) => {
    const custom = productCustomizations.find(c => c.id === customId);
    if (!custom) return;

    const ingredientName = prompt('Ingredient naam:', custom.ingredient_name);
    if (ingredientName === null) return;

    const extraPrice = prompt('Extra prijs (in centen):', custom.extra_price_cents || 0);
    if (extraPrice === null) return;

    const canBeRemoved = window.confirm('Kan dit ingredient verwijderd worden?');
    const canAddExtra = window.confirm('Kan dit ingredient extra toegevoegd worden?');

    updateProductCustomization(customId, {
      ingredient_name: ingredientName,
      extra_price_cents: parseInt(extraPrice) || 0,
      can_be_removed: canBeRemoved,
      can_add_extra: canAddExtra
    });
  };

  const updateProductCustomization = async (customId, updates) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen customizations bewerken');
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('product_customizations')
        .update(updates)
        .eq('id', customId);

      if (error) throw error;

      await loadProductCustomizations();
      alert('Customization succesvol bijgewerkt!');
    } catch (error) {
      console.error('Error updating product customization:', error);
      alert('Fout bij bijwerken van customization');
    }
  };

  const addProductCustomization = () => {
    const ingredientName = prompt('Ingredient naam:');
    if (!ingredientName) return;

    const extraPrice = prompt('Extra prijs (in centen):', '0');
    if (extraPrice === null) return;

    const canBeRemoved = window.confirm('Kan dit ingredient verwijderd worden?');
    const canAddExtra = window.confirm('Kan dit ingredient extra toegevoegd worden?');

    createProductCustomization({
      ingredient_name: ingredientName,
      extra_price_cents: parseInt(extraPrice) || 0,
      can_be_removed: canBeRemoved,
      can_add_extra: canAddExtra,
      sort_index: productCustomizations.length
    });
  };

  const createProductCustomization = async (customData) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen customizations toevoegen');
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('product_customizations')
        .insert({
          ...customData,
          product_id: productId
        });

      if (error) throw error;

      await loadProductCustomizations();
      alert('Customization succesvol toegevoegd!');
    } catch (error) {
      console.error('Error creating product customization:', error);
      alert('Fout bij toevoegen van customization');
    }
  };

  const deleteProductCustomization = async (customId) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen customizations verwijderen');
      return;
    }

    if (!window.confirm('Weet je zeker dat je deze customization wilt verwijderen?')) {
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      const { error } = await kmfcClient
        .from('product_customizations')
        .delete()
        .eq('id', customId);

      if (error) throw error;

      await loadProductCustomizations();
      alert('Customization succesvol verwijderd!');
    } catch (error) {
      console.error('Error deleting product customization:', error);
      alert('Fout bij verwijderen van customization');
    }
  };

  const deleteProductOption = async (optionId) => {
    if (!hasPermission('admin')) {
      alert('Alleen admins kunnen opties verwijderen');
      return;
    }

    if (!window.confirm('Weet je zeker dat je deze optie wilt verwijderen?')) {
      return;
    }

    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client niet beschikbaar');

      // Delete product option values first
      await kmfcClient
        .from('product_option_values')
        .delete()
        .eq('product_option_id', optionId);

      // Then delete the product option
      const { error } = await kmfcClient
        .from('product_options')
        .delete()
        .eq('id', optionId);

      if (error) throw error;

      // Reload options
      await loadProductOptions();
      alert('Optie succesvol verwijderd!');
    } catch (error) {
      console.error('Error deleting product option:', error);
      alert('Fout bij verwijderen van optie');
    }
  };

  const goBack = () => {
    navigate('/KMFC/settings/product-status');
  };

  if (loading) return <WebvonkLoader fullscreen text="Product wordt geladen..." />;

  if (!product) {
    return (
      <Container>
        <Header>
          <HeaderTitle>Product Details</HeaderTitle>
        </Header>
        <LoadingState>Product niet gevonden</LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={goBack}>
          <ChevronLeft size={24} />
        </BackButton>
        <HeaderTitle>Product Details</HeaderTitle>
      </Header>

      <Content>
        <div style={{
          background: '#1c1c1e',
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '28px 24px',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px',
              background: 'rgba(234,114,17,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', flexShrink: 0,
            }}>📱</div>
            <div>
              <div style={{ fontSize: '17px', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>
                Product aanpassen?
              </div>
              <div style={{ fontSize: '13px', color: '#888' }}>
                Gebruik de <span style={{ color: '#ea7211', fontWeight: 600 }}>Mijn Webvonk V2</span> app
              </div>
            </div>
          </div>
          <div style={{
            background: '#252525',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '14px',
            color: '#ccc',
            lineHeight: '1.7',
          }}>
            Ga in de app naar <strong style={{ color: '#fff' }}>Instellingen → Menu</strong> om producten aan te passen.<br />
          </div>
        </div>
        <div style={{ pointerEvents: 'none', userSelect: 'none', visibility: 'hidden' }}>
        <ProductDetailContainer>
          <ProductHeader>
            <ProductImage 
              src={product.image_url || '/placeholder.png'} 
              alt={product.name} 
            />
            <ProductInfo>
              <ProductName>{product.name}</ProductName>
              <ProductMeta>
                <MetaItem>
                  <MetaLabel>Prijs:</MetaLabel>
                  <MetaValue>€{(product.price_cents / 100).toFixed(2)}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Menu Nummer:</MetaLabel>
                  <MetaValue>{product.Productnummer || '-'}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Categorie:</MetaLabel>
                  <MetaValue>{product.categories?.name || '-'}</MetaValue>
                </MetaItem>
              </ProductMeta>
            </ProductInfo>
          </ProductHeader>

          {hasPermission('admin') && (
            <Form onSubmit={saveProduct}>
              <Section>
                <SectionTitle>📋 Basis Informatie</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Productnaam</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Menu Nummer</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.Productnummer}
                      onChange={(e) => handleInputChange('Productnummer', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <FormLabel>Beschrijving</FormLabel>
                  <FormTextarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </FormGroup>
              </Section>

              <Section>
                <SectionTitle>💰 Prijs en Weergave</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Prijs (in centen)</FormLabel>
                    <FormInput
                      type="number"
                      value={formData.price_cents}
                      onChange={(e) => handleInputChange('price_cents', e.target.value)}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Sorteer Volgorde binnen Categorie</FormLabel>
                    <FormInput
                      type="number"
                      value={formData.sort_index}
                      onChange={(e) => handleInputChange('sort_index', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <FormLabel>Categorie</FormLabel>
                    <FormSelect
                      value={formData.category_id}
                      onChange={(e) => handleInputChange('category_id', e.target.value)}
                      required
                    >
                      <option value="">Selecteer een categorie...</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name} (positie: {category.position})
                        </option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Afbeelding URL</FormLabel>
                    <FormInput
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => handleInputChange('image_url', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
              </Section>

              <Section>
                <SectionTitle>⚙️ Product Opties</SectionTitle>
                <FormRow>
                  <FormCheckbox>
                    <input
                      type="checkbox"
                      id="toon_extra_saus"
                      checked={formData.toon_extra_saus}
                      onChange={(e) => handleInputChange('toon_extra_saus', e.target.checked)}
                    />
                    <label htmlFor="toon_extra_saus">
                      Toon Sauzen en Dranken Pop-up
                    </label>
                  </FormCheckbox>
                  <FormCheckbox>
                    <input
                      type="checkbox"
                      id="product_customizations"
                      checked={formData.product_customizations}
                      onChange={(e) => handleInputChange('product_customizations', e.target.checked)}
                    />
                    <label htmlFor="product_customizations">
                      Product Customizations Toestaan
                    </label>
                  </FormCheckbox>
                </FormRow>
              </Section>

              <Section>
                <SectionTitle>🔧 Product Opties Beheer</SectionTitle>
                <OptionsList>
                  <div>
                    <h4>Product Opties ({productOptions.length})</h4>
                    {productOptions.map(option => (
                      <OptionCard key={option.id}>
                        <OptionHeader>
                          <OptionTitle>{option.name}</OptionTitle>
                          <OptionActions>
                            <IconButton onClick={() => editProductOption(option.id)}>
                              <Edit size={16} />
                              Bewerken
                            </IconButton>
                            <IconButton danger onClick={() => deleteProductOption(option.id)}>
                              <Trash2 size={16} />
                              Verwijderen
                            </IconButton>
                          </OptionActions>
                        </OptionHeader>
                        <OptionDetails>Type: {option.type} | Required: {option.required ? 'Ja' : 'Nee'}</OptionDetails>
                        <OptionDetails>Min select: {option.min_select} | Max select: {option.max_select}</OptionDetails>
                      </OptionCard>
                    ))}
                    <AddButton type="button" onClick={addProductOption}>
                      <Plus size={20} />
                      Optie Toevoegen
                    </AddButton>
                  </div>
                </OptionsList>
              </Section>

              <Section>
                <SectionTitle>🥗 Product Customizations Beheer</SectionTitle>
                <OptionsList>
                  <div>
                    <h4>Product Customizations ({productCustomizations.length})</h4>
                    {productCustomizations.map(custom => (
                      <OptionCard key={custom.id}>
                        <OptionHeader>
                          <OptionTitle>{custom.ingredient_name}</OptionTitle>
                          <OptionActions>
                            <IconButton onClick={() => editProductCustomization(custom.id)}>
                              <Edit size={16} />
                              Bewerken
                            </IconButton>
                            <IconButton danger onClick={() => deleteProductCustomization(custom.id)}>
                              <Trash2 size={16} />
                              Verwijderen
                            </IconButton>
                          </OptionActions>
                        </OptionHeader>
                        <p>Extra prijs: €{custom.extra_price_cents ? (custom.extra_price_cents / 100).toFixed(2) : '0.00'}</p>
                        <p>Verwijderbaar: {custom.can_be_removed ? 'Ja' : 'Nee'} | Extra toevoegen: {custom.can_add_extra ? 'Ja' : 'Nee'}</p>
                      </OptionCard>
                    ))}
                    <AddButton type="button" onClick={addProductCustomization}>
                      <Plus size={20} />
                      Customization Toevoegen
                    </AddButton>
                  </div>
                </OptionsList>
              </Section>

              <SaveButton type="submit" disabled={saving}>
                <Save size={20} />
                {saving ? 'Opslaan...' : 'Product Opslaan'}
              </SaveButton>
            </Form>
          )}
        </ProductDetailContainer>
        </div>
      </Content>
    </Container>
  );
}
